import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { brandConfig } from 'src/config';
import { GuestGeneral } from 'src/models/GuestGeneral';
import { ReservationGeneral } from 'src/models/ReservationGeneral';
import { UserAccountGeneral } from 'src/models/UserAccountGeneral';
import * as Yup from 'yup';
import FormProvider from '../../components/hook-form/FormProvider';
import {
  useGetSingleReservationQuery,
  usePatchRegistrationStepMutation,
} from '../../redux/rtkQuery/apiSlice';
import HelpLink from './HelpLinks';
import { RegistrationFooter } from './RegistrationFooter';
import { RegistrationPicklistItem } from './types';

interface RegistrationIdentityFormProps {
  stepCompleted: boolean;
  stepFinal: boolean;
  reservation: ReservationGeneral;
  callbackRefreshReservation: (overrideBusy: boolean) => void;
  callbackContinue: VoidFunction;
  enforceSessionIsMain: boolean;
  mainGuest: GuestGeneral | null;
  sessionUser: UserAccountGeneral | null;
  guestsLoading?: boolean;
}

type IdentityPhase = 'polling' | 'complete' | 'error';

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_TIME_MS = 2 * 60 * 1000;
const MAX_POLL_HITS = MAX_POLL_TIME_MS / POLL_INTERVAL_MS;
const COMPLETION_FEEDBACK_MS = 1500;

const POLL_TIMEOUT_MESSAGE =
  "We're still confirming your verification. Please wait a moment and refresh.";

const POLL_STATUS_MESSAGES = [
  'Confirming your identity…',
  'Reviewing your rental agreement…',
] as const;
const POLL_STATUS_ROTATE_MS = 3500;

function isIdentityStepComplete(reservation: ReservationGeneral): boolean {
  if (reservation.guest_registration_complete) {
    return true;
  }
  return Boolean(
    reservation.guest_registration_completed?.includes(RegistrationPicklistItem.ID_VERIFICATION)
  );
}

function isRentalAgreementCompleteInSf(reservation: ReservationGeneral): boolean {
  return Boolean(
    reservation.guest_registration_completed?.includes(RegistrationPicklistItem.RENTAL_AGREEMENT)
  );
}

export function RegistrationIdentityForm({
  stepCompleted,
  stepFinal,
  reservation,
  callbackRefreshReservation,
  callbackContinue,
  enforceSessionIsMain,
  mainGuest,
  sessionUser,
  guestsLoading = false,
}: RegistrationIdentityFormProps) {
  const hasSubmittedIdPatch = useRef(false);
  const pollTimerId = useRef<ReturnType<typeof setInterval> | null>(null);

  const [phase, setPhase] = useState<IdentityPhase>(() =>
    stepCompleted || isIdentityStepComplete(reservation) ? 'complete' : 'polling'
  );
  const [pollHits, setPollHits] = useState(0);
  const [showCompletionFeedback, setShowCompletionFeedback] = useState(false);
  const [pollStatusIndex, setPollStatusIndex] = useState(0);

  const {
    isFetching: reservationFetching,
    data: reservationData,
    refetch: refetchReservation,
  } = useGetSingleReservationQuery({ reservationName: reservation.name }, {});

  const [sendPatchRegistrationStep] = usePatchRegistrationStepMutation();

  const methods = useForm({
    defaultValues: {},
    resolver: yupResolver(Yup.object().shape({})),
    mode: 'all',
  });

  const latestReservation = reservationData ?? reservation;

  const patchIdentityStep = useCallback(async () => {
    if (hasSubmittedIdPatch.current) return;
    hasSubmittedIdPatch.current = true;

    const result = await sendPatchRegistrationStep({
      reservationName: reservation.name,
      registrationStep: RegistrationPicklistItem.ID_VERIFICATION,
    });
    const err = (result as { error?: Error }).error;
    if (err) {
      hasSubmittedIdPatch.current = false;
      console.error('error on patch identity verification step', err.message);
    }
  }, [reservation.name, sendPatchRegistrationStep]);

  useEffect(() => {
    if (stepCompleted || phase !== 'polling') {
      if (pollTimerId.current) {
        clearInterval(pollTimerId.current);
        pollTimerId.current = null;
      }
      return () => {
        if (pollTimerId.current) clearInterval(pollTimerId.current);
      };
    }

    //NOTE: No immediate refetch here. The DocuSign return is a full-page redirect, so the
    //      reservation query has just fetched fresh data on mount. Refetching during the mount
    //      effect would also throw, because parent queries have not started yet at that point.
    if (pollTimerId.current) return undefined;
    pollTimerId.current = setInterval(() => {
      setPollHits((prev) => prev + 1);
      callbackRefreshReservation(true);
      refetchReservation();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerId.current) {
        clearInterval(pollTimerId.current);
        pollTimerId.current = null;
      }
    };
  }, [callbackRefreshReservation, phase, refetchReservation, stepCompleted]);

  useEffect(() => {
    if (phase !== 'polling' || stepCompleted) {
      return undefined;
    }
    const timerId = setInterval(() => {
      setPollStatusIndex((prev) => (prev + 1) % POLL_STATUS_MESSAGES.length);
    }, POLL_STATUS_ROTATE_MS);
    return () => clearInterval(timerId);
  }, [phase, stepCompleted]);

  useEffect(() => {
    if (phase !== 'polling' || stepCompleted) return;

    if (pollHits > MAX_POLL_HITS) {
      setPhase('error');
      return;
    }

    if (isIdentityStepComplete(latestReservation)) {
      setPhase('complete');
      setShowCompletionFeedback(true);
      return;
    }

    if (
      isRentalAgreementCompleteInSf(latestReservation) &&
      !latestReservation.guest_registration_completed?.includes(
        RegistrationPicklistItem.ID_VERIFICATION
      )
    ) {
      patchIdentityStep().catch(() => {});
    }
  }, [latestReservation, patchIdentityStep, phase, pollHits, stepCompleted]);

  useEffect(() => {
    if (!showCompletionFeedback) return undefined;
    const t = setTimeout(() => {
      setShowCompletionFeedback(false);
      callbackContinue();
    }, COMPLETION_FEEDBACK_MS);
    return () => clearTimeout(t);
  }, [callbackContinue, showCompletionFeedback]);

  const manuallyRefetch = useCallback(() => {
    if (reservationFetching) return;
    setPollHits(0);
    setPhase('polling');
    refetchReservation();
    callbackRefreshReservation(true);
  }, [callbackRefreshReservation, refetchReservation, reservationFetching]);

  const userInfoLoaded = mainGuest !== null && sessionUser !== null;
  const stepAllowedForUser = enforceSessionIsMain
    ? userInfoLoaded && mainGuest.guest_id === sessionUser.externalAccountID
    : true;

  const continueVerb = stepFinal ? 'Finish' : 'Continue';

  if (guestsLoading) {
    return (
      <FormProvider
        name="Identity Step Form"
        id={`Identity Step Form: ${reservation.name}`}
        methods={methods}
      >
        <Stack spacing={2} alignItems="center" py={4} minHeight={120}>
          <CircularProgress />
          <Typography>Loading…</Typography>
        </Stack>
      </FormProvider>
    );
  }

  if (enforceSessionIsMain && !userInfoLoaded) {
    return (
      <FormProvider
        name="Identity Step Form"
        id={`Identity Step Form: ${reservation.name}`}
        methods={methods}
      >
        <Alert severity="error">User info not loaded.</Alert>
      </FormProvider>
    );
  }

  if (!stepAllowedForUser) {
    return (
      <FormProvider
        name="Identity Step Form"
        id={`Identity Step Form: ${reservation.name}`}
        methods={methods}
      >
        <Alert severity="error">
          You are not authorized to complete this step. Only the main host may verify identity.
        </Alert>
      </FormProvider>
    );
  }

  if (stepCompleted) {
    return (
      <FormProvider
        name="Identity Step Form"
        id={`Identity Step Form: ${reservation.name}`}
        methods={methods}
      >
        <Stack spacing={2}>
          <Alert severity="info" style={{ fontSize: '1.1rem' }}>
            Identity verified.
          </Alert>
          <RegistrationFooter>
            <LoadingButton variant="contained" onClick={callbackContinue}>
              {continueVerb}
            </LoadingButton>
          </RegistrationFooter>
        </Stack>
      </FormProvider>
    );
  }

  if (showCompletionFeedback || phase === 'complete') {
    return (
      <FormProvider
        name="Identity Step Form"
        id={`Identity Step Form: ${reservation.name}`}
        methods={methods}
      >
        <Alert severity="success" style={{ fontSize: '1.1rem' }}>
          Identity verified. Finishing registration…
        </Alert>
      </FormProvider>
    );
  }

  if (phase === 'error') {
    return (
      <FormProvider
        name="Identity Step Form"
        id={`Identity Step Form: ${reservation.name}`}
        methods={methods}
      >
        <Stack spacing={2}>
          <Alert severity="warning" style={{ fontSize: '1.1rem' }}>
            {POLL_TIMEOUT_MESSAGE}
          </Alert>
          {brandConfig.helpLinks.guest_id_verification && (
            <HelpLink
              label="guest_registration"
              value={brandConfig.helpLinks.guest_id_verification}
            />
          )}
          <RegistrationFooter>
            <Button variant="contained" onClick={manuallyRefetch} disabled={reservationFetching}>
              Refresh status
            </Button>
          </RegistrationFooter>
        </Stack>
      </FormProvider>
    );
  }

  return (
    <FormProvider
      name="Identity Step Form"
      id={`Identity Step Form: ${reservation.name}`}
      methods={methods}
    >
      <Stack spacing={2} alignItems="center" py={4} minHeight={150}>
        <CircularProgress />
        <Typography textAlign="center">{POLL_STATUS_MESSAGES[pollStatusIndex]}</Typography>
        {brandConfig.helpLinks.guest_id_verification && (
          <HelpLink label="guest_registration" value={brandConfig.helpLinks.guest_id_verification} />
        )}
      </Stack>
    </FormProvider>
  );
}
